import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Phone, Mail, Clock, Search, User, Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { categories } from '../lib/categories';
import { cn } from '../lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
const LOGO_URL = 'https://customuniformsupplier.com/cdn/shop/files/T1-Logo-Black.png';
const TopBar = () => <div className="bg-ink text-gray-200 text-[13px] py-2.5 px-5 sm:px-8 lg:px-12 border-b border-white/5" data-id="element-193">
    <div className="max-w-content mx-auto flex flex-col sm:flex-row justify-between items-center gap-2" data-id="element-194">
      <div className="flex items-center gap-6 font-medium" data-id="element-195">
        <span className="hidden md:inline" data-id="element-196">
          Wholesale custom uniforms and team apparel
        </span>
        <span className="hidden lg:inline text-gray-400" data-id="element-197">
          Private-label production for approved wholesale buyers
        </span>
      </div>
      <div className="flex items-center gap-6 font-medium" data-id="element-198">
        <a href="tel:8886998511" className="flex items-center gap-1.5 hover:text-white transition-colors" data-id="element-199">
          <Phone className="w-3.5 h-3.5" data-id="element-200" /> (888) 699-8511
        </a>
        <a href="mailto:info@customuniformsupplier.com" className="hidden md:flex items-center gap-1.5 hover:text-white transition-colors" data-id="element-201">
          <Mail className="w-3.5 h-3.5" data-id="element-202" /> info@customuniformsupplier.com
        </a>
      </div>
    </div>
  </div>;
const HeaderMicrocopy = () => <div className="bg-gray50 border-b border-gray200 py-1.5 px-5 sm:px-8 lg:px-12 hidden md:block text-center" data-id="element-203">
    <p className="text-[13px] text-gray-500 font-medium" data-id="element-204">
      Dealers, brands, teams, and end users can create an account to start
      designing.
    </p>
  </div>;
// Individual sports surfaced on the Homepage (SeasonalSportsSection). The
// Products nav dropdown lists exactly these and links to each category page.
const homepageSports = ['football', 'soccer', 'baseball', 'softball', 'basketball', 'volleyball'];
const getCat = (slug: string) => categories.find(c => c.slug === slug);
const navItems = [{
  label: 'Home',
  href: '/'
}, {
  label: 'Products',
  href: '/products',
  dropdown: [{
    label: 'Featured / Seasonal',
    href: '/products'
  }, {
    label: 'Team Sports',
    href: '/products'
  }, {
    label: 'Specialty / Apparel',
    href: '/products'
  }]
}, {
  label: 'For Dealers',
  href: '/wholesale-apply'
}, {
  label: 'Why Tier One',
  href: '/why-tier-one'
}, {
  label: 'How It Works',
  href: '/how-it-works'
}, {
  label: 'Production & Support',
  href: '/production-support'
}, {
  label: 'Contact',
  href: '/contact'
}];
const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);
  return <>
      <header className="bg-white border-b border-gray200 sticky top-0 z-50" data-id="element-205">
        <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between gap-6" data-id="element-206">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-id="element-207">
            <img src={LOGO_URL} alt="T1 Logo" className="h-10 w-auto" data-id="element-208" />
            <div className="hidden lg:block border-l border-gray200 pl-3 ml-1" data-id="element-209">
              <div className="text-sm font-bold tracking-wide text-charcoal uppercase leading-tight" data-id="element-210">
                Tier 1 Apparel
              </div>
              <div className="text-xs text-gray-500 leading-tight mt-0.5" data-id="element-211">
                Built To Elevate Your Brand
              </div>
            </div>
          </Link>

          {/* Desktop CTA cluster — Use Designer / Create Account / Apply for Wholesale Access */}
          <div className="hidden lg:flex items-center gap-4 shrink-0" data-id="element-212">
            <Link to="/products" className="text-[13px] font-bold text-gray-600 hover:text-brandRed transition-colors uppercase tracking-wider" data-id="element-213">
              Start Designing
            </Link>
            <Link to="/wholesale-apply" className="text-[13px] font-bold text-ink hover:text-brandRed transition-colors uppercase tracking-wider px-2" data-id="element-214">
              Create Account
            </Link>
            <Link to="/wholesale-apply" className="bg-brandRed hover:bg-brandRedDark text-white text-[13px] font-bold py-2.5 px-5 rounded-sm transition-colors uppercase tracking-wider shadow-sm" data-id="element-215">
              Apply for Wholesale Access
            </Link>
          </div>

          <button className="xl:hidden p-2 -mr-2 text-ink hover:bg-gray50 rounded-md transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-216">
            {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-217" /> : <Menu className="w-6 h-6" data-id="element-218" />}
          </button>
        </div>

        {/* Mobile CTA cluster — surfaces all three actions */}
        <div className="lg:hidden" data-id="element-219">
          <div className="p-4 border-t border-gray200 flex flex-col gap-3 bg-gray50" data-id="element-220">
            <Link to="/products" className="w-full text-center text-[13px] font-bold text-gray-600 hover:text-brandRed transition-colors uppercase tracking-wider py-2" data-id="element-221">
              Start Designing
            </Link>
            <Link to="/wholesale-apply" className="w-full text-center text-[13px] font-bold text-ink border border-gray300 bg-white hover:bg-gray50 transition-colors uppercase tracking-wider py-3 rounded-sm" data-id="element-222">
              Create Account
            </Link>
            <Link to="/wholesale-apply" className="w-full bg-brandRed hover:bg-brandRedDark text-white text-[13px] font-bold py-3 rounded-sm transition-colors uppercase tracking-wider text-center shadow-sm" data-id="element-223">
              Apply for Wholesale Access
            </Link>
          </div>
        </div>
      </header>

      {/* Nav row */}
      <nav className="hidden lg:block border-t border-gray100 bg-white relative" data-id="element-224">
        {/* Click-outside overlay — closes the open dropdown without auto-closing on mouse movement */}
        {activeDropdown && <div className="fixed inset-0 z-30" aria-hidden="true" onClick={() => setActiveDropdown(null)} data-id="element-225" />}
        <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-center h-12 relative z-50" data-id="element-226">
          {navItems.map(item => {
          const isActive = location.pathname === item.href;
          return <div key={item.label} className="relative h-full" data-id="element-227">
                {item.dropdown ? <button className="h-full px-3.5 xl:px-4 text-[13px] xl:text-sm font-bold uppercase tracking-wide flex items-center gap-1.5 transition-colors border-b-2" onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)} data-id="element-228">
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" data-id="element-229" />
                  </button> : <Link to={item.href} className="h-full px-3.5 xl:px-4 text-[13px] xl:text-sm font-bold uppercase tracking-wide flex items-center transition-colors border-b-2" data-id="element-230">
                    {item.label}
                  </Link>}
              </div>;
        })}
        </div>

        {/* Products Mega Menu */}
        <AnimatePresence data-id="element-231">
          {activeDropdown === 'Products' && <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2
        }} className="absolute left-0 right-0 top-full bg-white border-t border-gray200 shadow-2xl z-50" data-id="element-232">
              <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12 py-8" data-id="element-233">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-5" data-id="element-234">
                  Shop by Sport
                </h4>
                <ul className="grid grid-cols-3 gap-x-10 gap-y-3" data-id="element-235">
                  {homepageSports.map(slug => {
                const cat = getCat(slug);
                if (!cat) return null;
                return <li key={slug} data-id="element-236">
                        <Link to={`/category/${slug}`} className="text-[15px] font-semibold text-charcoal hover:text-brandRed transition-colors group flex items-center justify-between" data-id="element-237">
                          <span data-id="element-238">{cat.name}</span>
                          <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-brandRed transition-colors" data-id="element-239" />
                        </Link>
                      </li>;
              })}
                </ul>
                <div className="mt-6 pt-5 border-t border-gray100" data-id="element-240">
                  <Link to="/products" className="text-sm font-bold text-brandRed hover:text-brandRedDark transition-colors flex items-center gap-1.5" data-id="element-241">
                    View All Products <ChevronRight className="w-4 h-4" data-id="element-242" />
                  </Link>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-243">
        {isMobileMenuOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="lg:hidden bg-white border-t border-gray200 overflow-hidden" data-id="element-244">
            <div className="px-4 py-6 flex flex-col gap-4 max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-245">
              <div className="flex flex-col gap-2.5" data-id="element-246">
                <Link to="/products" className="bg-brandRed text-white text-center font-bold py-3.5 rounded-sm text-base" data-id="element-247">
                  Start Your Design
                </Link>
                <Link to="/wholesale-apply" className="border-2 border-charcoal text-charcoal text-center font-bold py-3 rounded-sm text-base" data-id="element-248">
                  Apply for Wholesale Access
                </Link>
              </div>

              <ul className="flex flex-col mt-3 border-y border-gray100 divide-y divide-gray100" data-id="element-249">
                {navItems.map(item => <li key={item.label} data-id="element-250">
                    {item.dropdown ? <>
                        <button className="w-full flex items-center justify-between py-3.5 text-left font-bold text-charcoal text-base" onClick={() => setActiveDropdown(item.label)} data-id="element-251">
                          {item.label}
                          <ChevronDown className={cn('w-5 h-5 transition-transform', activeDropdown === item.label && 'rotate-180')} data-id="element-252" />
                        </button>
                        <AnimatePresence data-id="element-253">
                          {activeDropdown === item.label && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="overflow-hidden" data-id="element-254">
                              <div className="pb-3 flex flex-col" data-id="element-255">
                                {homepageSports.map(slug => {
                        const cat = getCat(slug);
                        if (!cat) return null;
                        return <Link key={slug} to={`/category/${slug}`} className="text-[15px] text-gray-700 py-2.5 pl-4" data-id="element-256">
                                      {cat.name}
                                    </Link>;
                      })}
                                <Link to="/products" className="text-[15px] font-bold text-brandRed py-2.5 pl-4" data-id="element-257">
                                  View All Products
                                </Link>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </> : <Link to={item.href} className="py-3.5 font-bold text-charcoal text-base flex items-center justify-between" data-id="element-258">
                        {item.label}
                        <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-259" />
                      </Link>}
                  </li>)}
              </ul>

              <button className="text-base font-semibold text-charcoal py-2 flex items-center justify-between mt-2" data-id="element-260">
                <span className="flex items-center gap-2" data-id="element-261">
                  <User className="w-5 h-5" data-id="element-262" /> Sign In / Account
                </span>
                <ChevronRight className="w-5 h-5" data-id="element-263" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
};
const Footer = () => {
  const footerCols = [{
    title: 'Company',
    links: [{
      label: 'Home',
      to: '/'
    }, {
      label: 'Why Tier One',
      to: '/why-tier-one'
    }, {
      label: 'How It Works',
      to: '/how-it-works'
    }, {
      label: 'Contact',
      to: '/contact'
    }, {
      label: 'Production & Support',
      to: '/production-support'
    }]
  }, {
    title: 'Wholesale',
    links: [{
      label: 'Apply for Wholesale Access',
      to: '/wholesale-apply'
    }, {
      label: 'Dealer Support',
      to: '/dealer-support'
    }, {
      label: 'Private-Label Production',
      to: '/private-label'
    }, {
      label: 'Combined Quantity Pricing',
      to: '/combined-pricing'
    }, {
      label: 'Account Sign In',
      to: '/login'
    }]
  }, {
    title: 'Teams & Organizations',
    links: [{
      label: 'Create a Team Account',
      to: '/wholesale-apply'
    }, {
      label: 'Submit Team Request',
      to: '/wholesale-apply'
    }, {
      label: 'Schools & Leagues',
      to: '/wholesale-apply'
    }, {
      label: 'Team Gear Support',
      to: '/wholesale-apply'
    }]
  }, {
    title: 'Design & Samples',
    links: [{
      label: 'Start Your Design',
      to: '/products'
    }, {
      label: 'Customize Your Uniform',
      to: '/products'
    }, {
      label: 'Size Samples',
      to: '/samples/size'
    }, {
      label: 'Product Samples',
      to: '/samples/product'
    }, {
      label: 'Order Support',
      to: '/order-support'
    }]
  }];
  return <footer className="bg-ink text-white pt-20 pb-8" data-id="element-264">
      <div className="max-w-content mx-auto px-5 sm:px-8 lg:px-12" data-id="element-265">
        {/* Top: Brand + CTAs */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mb-16 pb-16 border-b border-white/10" data-id="element-266">
          <div className="lg:col-span-4" data-id="element-267">
            <img src={LOGO_URL} alt="T1 Logo" className="h-10 w-auto mb-6 brightness-0 invert" data-id="element-268" />
            <p className="text-gray-400 text-[15px] leading-relaxed mb-8 max-w-sm" data-id="element-269">
              Wholesale custom uniforms and private-label apparel production for
              dealers, resellers, and brands.
            </p>
            <div className="flex items-center gap-4" data-id="element-270">
              <Link to="/wholesale-apply" className="bg-brandRed hover:bg-brandRedDark text-white font-bold py-3 px-6 rounded-sm transition-colors text-sm" data-id="element-271">
                Apply for Wholesale
              </Link>
            </div>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-8" data-id="element-272">
            <div data-id="element-273">
              <h4 className="text-white font-bold mb-5 uppercase tracking-wider text-sm" data-id="element-274">
                Quick Links
              </h4>
              <ul className="flex flex-col gap-3" data-id="element-275">
                {[{
                label: 'Apply for Wholesale Access',
                href: '/wholesale-apply'
              }, {
                label: 'Create Account',
                href: '/wholesale-apply'
              }, {
                label: 'Start Designing',
                href: '/products'
              }, {
                label: 'Need Help Ordering?',
                href: '/wholesale-apply'
              }, {
                label: 'Dealer Support',
                href: '/wholesale-apply'
              }].map(link => <li key={link.label} data-id="element-276">
                    <Link to={link.href} className="text-gray-400 hover:text-white transition-colors text-[15px]" data-id="element-277">
                      {link.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-278">
              <h4 className="text-white font-bold mb-5 uppercase tracking-wider text-sm" data-id="element-279">
                Support
              </h4>
              <ul className="flex flex-col gap-3" data-id="element-280">
                {[{
                label: 'Order Support',
                href: '/order-support'
              }, {
                label: 'Design Policy',
                href: '/design-policy'
              }, {
                label: 'Returns Policy',
                href: '/returns'
              }, {
                label: 'Privacy Policy',
                href: '/privacy'
              }].map(link => <li key={link.label} data-id="element-281">
                    <Link to={link.href} className="text-gray-400 hover:text-white transition-colors text-[15px]" data-id="element-282">
                      {link.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-283">
              <h4 className="text-white font-bold mb-5 uppercase tracking-wider text-sm" data-id="element-284">
                About
              </h4>
              <ul className="flex flex-col gap-3" data-id="element-285">
                {[{
                label: 'Why Tier One',
                href: '/why-tier-one'
              }, {
                label: 'How It Works',
                href: '/how-it-works'
              }, {
                label: 'Production & Support',
                href: '/production-support'
              }, {
                label: 'Contact',
                href: '/contact'
              }].map(link => <li key={link.label} data-id="element-286">
                    <Link to={link.href} className="text-gray-400 hover:text-white transition-colors text-[15px]" data-id="element-287">
                      {link.label}
                    </Link>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* Categories row */}
        <div className="mb-12 pb-12 border-b border-white/10" data-id="element-288">
          <h4 className="font-heading font-bold text-white mb-6 uppercase tracking-wider text-sm" data-id="element-289">
            Categories
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-x-4 gap-y-3 text-[14px]" data-id="element-290">
            {categories.map(cat => <Link key={cat.slug} to={`/category/${cat.slug}`} className="text-gray-300 hover:text-white transition-colors font-medium" data-id="element-291">
                {cat.name}
              </Link>)}
          </div>
        </div>

        {/* Bottom row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-292">
          <div className="text-gray-400 text-sm" data-id="element-293">
            © 2026 Custom Uniform Supplier / Tier One Apparel
          </div>
          <div className="flex flex-wrap gap-5 text-sm text-gray-400" data-id="element-294">
            <Link to="/terms" className="hover:text-white transition-colors" data-id="element-295">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-id="element-296">
              Privacy Policy
            </Link>
            <Link to="/returns" className="hover:text-white transition-colors" data-id="element-297">
              Returns Policy
            </Link>
            <Link to="/design-policy" className="hover:text-white transition-colors" data-id="element-298">
              Design Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>;
};
export const Layout = () => {
  return <div className="min-h-screen flex flex-col font-sans text-charcoal bg-white" data-id="element-299">
      <TopBar data-id="element-300" />
      <HeaderMicrocopy data-id="element-301" />
      <Header data-id="element-302" />
      <main className="flex-grow" data-id="element-303">
        <Outlet data-id="element-304" />
      </main>
      <Footer data-id="element-305" />
    </div>;
};